import { findExerciseByName, normalizeExerciseName } from '../utils/exerciseIdentity';

// Deterministic quick progressions/regressions for verified movement families.
// The exercise database can enrich these records when populated, but these
// core choices do not disappear when the database is empty.
//
// User skill level controls workout generation; it does not hide a user-selected
// low-impact or advanced variation.

const makeOption = (name, difficulty, muscle, requiredEquipment = null) => ({
  exercise_name: name,
  name,
  difficulty,
  muscle_group: muscle,
  muscle,
  equipment: 'bodyweight',
  requiredEquipment,
  source: 'curated_progression',
});

const FAMILIES = [
  {
    identities: ['jumping jack', 'step out jack', 'power jack'],
    lowImpact: makeOption('Step-Out Jack', 'beginner', 'Cardio Warmup'),
    standard: makeOption('Jumping Jacks', 'intermediate', 'Cardio Warmup'),
    advanced: makeOption('Power Jack', 'advanced', 'Cardio Warmup'),
  },
  {
    identities: ['push up', 'incline push up', 'decline push up'],
    lowImpact: makeOption('Incline Push-Up', 'beginner', 'Chest', 'raised_surface'),
    standard: makeOption('Push-Up', 'intermediate', 'Chest'),
    advanced: makeOption('Decline Push-Up', 'advanced', 'Chest', 'raised_surface'),
  },
];

const sameIdentity = (left, right) =>
  normalizeExerciseName(left) === normalizeExerciseName(right);

const findFamily = (exerciseName) => {
  const identity = normalizeExerciseName(exerciseName);
  if (!identity) return null;

  return FAMILIES.find((family) =>
    family.identities.some((candidate) => sameIdentity(candidate, identity))
  ) || null;
};

const equipmentAllowed = (candidate, context = {}) => {
  if (!candidate) return false;
  if (context.workoutType !== 'home') return true;

  if (!candidate.requiredEquipment) return true;

  // A stable chair, bench, counter or step is a normal environmental support,
  // not specialized gym equipment.
  if (candidate.requiredEquipment === 'raised_surface') return true;

  return false;
};

const enrich = (candidate, library = []) => {
  if (!candidate) return null;

  const record = findExerciseByName(library, candidate.exercise_name);
  if (!record) return candidate;

  return {
    ...candidate,
    ...record,
    exercise_name: record.exercise_name || record.name || candidate.exercise_name,
    name: record.name || record.exercise_name || candidate.name,
    requiredEquipment: candidate.requiredEquipment,
  };
};

export const getQuickExerciseAlternatives = (
  exercise,
  library = [],
  context = {}
) => {
  const currentName = exercise?.exercise_name || exercise?.name || '';
  const family = findFamily(currentName);

  if (!family) {
    return {
      family: null,
      lowImpact: null,
      standard: null,
      advanced: null,
    };
  }

  return {
    family,
    lowImpact: equipmentAllowed(family.lowImpact, context)
      ? enrich(family.lowImpact, library)
      : null,
    standard: equipmentAllowed(family.standard, context)
      ? enrich(family.standard, library)
      : null,
    advanced: equipmentAllowed(family.advanced, context)
      ? enrich(family.advanced, library)
      : null,
  };
};

export const isCurrentExerciseOption = (exercise, option) =>
  Boolean(
    exercise &&
    option &&
    sameIdentity(
      exercise.exercise_name || exercise.name,
      option.exercise_name || option.name
    )
  );
